import React from 'react';
import { X, Minus, Plus, ShoppingBag, ShieldCheck } from 'lucide-react';
import { CartItem } from '../types';
import { CONTACT_INFO } from '../constants';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
  onContinueShopping: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onContinueShopping
}) => {
  const total = cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  const formattedTotal = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(total);

  const handleCheckout = () => {
    let message = "Hi, I'd like to place an order:\n\n";
    cartItems.forEach(item => {
      message += `- ${item.quantity} x ${item.name} @ ${new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(item.price || 0)}\n`;
    });
    message += `\nTotal: ${formattedTotal}`;
    const url = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-earth-50">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-herbal-800" />
            <h2 className="font-heading text-xl font-bold text-herbal-900">Your Cart ({cartItems.length})</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar">
          {cartItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-50">
              <ShoppingBag className="h-16 w-16 text-gray-300" />
              <p className="text-lg font-medium text-gray-500">Your cart is empty</p>
              <button onClick={onContinueShopping} className="text-herbal-800 font-bold hover:underline">Start Shopping</button>
            </div>
          ) : (
            cartItems.map(item => (
              <div key={item.id} className="flex gap-4 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="h-20 w-20 bg-gray-50 rounded-lg p-2 flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-bold text-gray-800 line-clamp-1">{item.name}</h3>
                    <p className="text-xs text-gray-500">{item.category}</p>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-8">
                       <button 
                         onClick={() => item.quantity > 1 ? onUpdateQuantity(item.id, -1) : onRemoveItem(item.id)}
                         className="px-2 h-full text-gray-500 hover:text-red-500 transition-colors"
                       >
                         <Minus className="h-3 w-3" />
                       </button>
                       <span className="w-8 text-center text-xs font-bold">{item.quantity}</span>
                       <button 
                         onClick={() => onUpdateQuantity(item.id, 1)}
                         className="px-2 h-full text-gray-500 hover:text-green-600 transition-colors"
                       >
                         <Plus className="h-3 w-3" />
                       </button>
                    </div>
                    <span className="font-bold text-herbal-900 text-sm">
                      {new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format((item.price || 0) * item.quantity)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t border-gray-100 bg-earth-50">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600 font-medium">Subtotal</span>
              <span className="text-2xl font-bold text-herbal-900">{formattedTotal}</span>
            </div>
            <div className="space-y-3">
              <button 
                onClick={handleCheckout}
                className="w-full bg-herbal-800 hover:bg-herbal-900 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
              >
                <ShieldCheck className="h-5 w-5" />
                Secure Checkout
              </button>
              
              <button 
                onClick={onContinueShopping}
                className="w-full bg-white border-2 border-gray-200 hover:border-herbal-800 text-gray-600 hover:text-herbal-800 font-bold py-3 rounded-xl transition-colors"
              >
                Continue Shopping
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              Processed securely via WhatsApp
            </p>
          </div>
        )}
      </div>
    </div>
  );
};